import React, { Suspense, lazy } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { CircularProgress, Box } from '@mui/material';

// Auth0 Authentication (eagerly loaded - needed for initial render)
import Auth0Login from './features/auth/components/Auth0Login';
import AuthRedirect from './features/auth/components/AuthRedirect';
import ProtectedRoute from './features/auth/components/ProtectedRoute';
import TopNavLayout from './features/dashboard/TopNavLayout';
import { UserAccessProvider, useUserAccess } from './context/UserAccessContext';
import { auth0Config } from './config/auth0Config';
import theme from './theme';
import { Outlet } from 'react-router-dom';

// Lazy load dashboard pages for better initial bundle size
const HomePage = lazy(() => import('./features/dashboard/pages/HomePage'));
const MyPlansPage = lazy(() => import('./features/dashboard/pages/MyPlansPage'));
const MyClientsPage = lazy(() => import('./features/dashboard/pages/MyClientsPage'));
const ClientDetailPage = lazy(() => import('./features/dashboard/pages/ClientDetailPage'));
const OpportunitiesPage = lazy(() => import('./features/dashboard/pages/OpportunitiesPage'));
const PlanEngagementPage = lazy(() => import('./features/dashboard/pages/PlanEngagementPage'));
const ResourcesPage = lazy(() => import('./features/dashboard/pages/ResourcesPage'));
const RothMethodologyPage = lazy(() => import('./features/dashboard/pages/RothMethodologyPage'));
const CustomerManagementPage = lazy(() => import('./features/dashboard/pages/CustomerManagementPage'));
const ReviewQueuePage = lazy(() => import('./features/dashboard/pages/ReviewQueuePage'));
const AnalyticsPage = lazy(() => import('./features/dashboard/pages/AnalyticsPage'));
const UtilizationPage = lazy(() => import('./features/dashboard/pages/UtilizationPage'));
const EnterpriseLayout = lazy(() => import('./features/enterprise/EnterpriseLayout'));
const EnterprisePage = lazy(() => import('./features/enterprise/EnterprisePage'));
const EnterprisePreferencesPage = lazy(() => import('./features/enterprise/EnterprisePreferencesPage'));
const PartnerIntegrationsPage = lazy(() => import('./features/dashboard/pages/PartnerIntegrationsPage'));

/** Shared auth/provider stack for both the advisor and enterprise shells. */
const DashboardShell: React.FC = () => (
  <ProtectedRoute>
    <UserAccessProvider>
      <Outlet />
    </UserAccessProvider>
  </ProtectedRoute>
);

/**
 * /dashboard landing: users with enterprise org assignments live in the
 * enterprise view; everyone else gets the advisor Home. While "view as
 * advisor" is active, enterprise users see that advisor's Home instead.
 */
const DashboardIndex: React.FC = () => {
  const { hasEnterpriseView, viewAsCustomerId } = useUserAccess();
  if (hasEnterpriseView && !viewAsCustomerId) {
    return <Navigate to="/dashboard/enterprise" replace />;
  }
  return <HomePage />;
};

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const App: React.FC = () => {
  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={auth0Config.authorizationParams}
      useRefreshTokens={auth0Config.useRefreshTokens}
      cacheLocation={auth0Config.cacheLocation}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<AuthRedirect />} />
              <Route path="/login" element={<Auth0Login />} />

              {/* Protected Dashboard Routes */}
              <Route path="/dashboard" element={<DashboardShell />}>
                {/* Advisor shell (pathless layout route — child paths unchanged) */}
                <Route element={<TopNavLayout />}>
                  <Route index element={<DashboardIndex />} />
                  <Route path="opportunities" element={<OpportunitiesPage />} />
                  <Route path="engagement" element={<PlanEngagementPage />} />
                  <Route path="resources" element={<ResourcesPage />} />
                  {/* Unlisted: reached only from the Home library card and the
                      no-optimizations banner, not from the Resources catalog. */}
                  <Route path="resources/roth-algorithm" element={<RothMethodologyPage />} />
                  <Route path="my-plans" element={<MyPlansPage />} />
                  <Route path="my-clients" element={<MyClientsPage />} />
                  <Route path="my-clients/:clientId" element={<ClientDetailPage />} />
                  <Route path="customer-management" element={<CustomerManagementPage />} />
                  <Route path="review-queue" element={<ReviewQueuePage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="utilization" element={<UtilizationPage />} />
                  <Route path="partner-integrations" element={<PartnerIntegrationsPage />} />
                  {/* Legacy routes - redirect to new paths */}
                  <Route path="external-webform" element={<Navigate to="/dashboard/my-plans" replace />} />
                  <Route path="clients" element={<Navigate to="/dashboard/my-clients" replace />} />
                  <Route path="users" element={<Navigate to="/dashboard/customer-management" replace />} />
                </Route>
                {/* Enterprise shell (firm/branch/team rollup view) */}
                <Route path="enterprise" element={<EnterpriseLayout />}>
                  <Route index element={<EnterprisePage />} />
                  <Route path="preferences" element={<EnterprisePreferencesPage />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ThemeProvider>
    </Auth0Provider>
  );
};

export default App;
